/** @format */

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import BlindRender from "../../components/BlindRender";
import { useDispatch, useSelector } from "react-redux";
import { calculateTotalPrice } from "../../actions/blindActions";
import { useLocation, useNavigate } from "react-router-dom";
import Stepper from "../../components/Stepper";
import { addEmailToCartMailChimp } from "../../actions/userActions";
import { addToCart, removeFromCart } from "../../actions/cartActions";
import { motion } from "framer-motion";
import { AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { ArrowRight } from "lucide-react";
import { Toast } from "./Toast";
import { Tooltip } from "./Tooltip";

export function getSimplyCutSize(width, measure) {
	let x;
	let y;
	width = parseFloat(width.toString());

	function roundDownToFraction(input) {
		const fractions = [
			"0",
			"1/16",
			"1/8",
			"3/16",
			"1/4",
			"5/16",
			"3/8",
			"7/16",
			"1/2",
			"9/16",
			"5/8",
			"11/16",
			"3/4",
			"13/16",
			"7/8",
			"15/16",
		];

		const fractionValues = [
			0,
			1 / 16,
			1 / 8,
			3 / 16,
			1 / 4,
			5 / 16,
			3 / 8,
			7 / 16,
			1 / 2,
			9 / 16,
			5 / 8,
			11 / 16,
			3 / 4,
			13 / 16,
			7 / 8,
			15 / 16,
		];

		for (let i = fractionValues.length - 1; i >= 0; i--) {
			if (input >= fractionValues[i]) {
				return fractions[i];
			}
		}
		return "0";
	}

	if (width <= 30) {
		x = 30;
	} else if (width <= 38) {
		x = 38;
	} else if (width <= 54) {
		x = 54;
	} else {
		x = 72;
	}
	y = measure === "Tight" ? 0.125 : 0;

	const cutSize = (x - width + y) / 2;
	const integerPart = Math.floor(cutSize);
	const fractionPart = roundDownToFraction(cutSize - integerPart);
	// const fractionPart = new Fraction(cutSize - integerPart).toString();

	return fractionPart == "0"
		? `${integerPart}`
		: `${integerPart} ${fractionPart}`;
}

const Breakdown = ({
	renderFabric,
	renderHem,
	renderValance,
	originalPrices, // Renamed from allPrices for clarity
	totalPrice, // Original total before discounts
	finalPrice, // OUR_PRICE, the discounted price
	displayCountryPrice,
	isCellular,
}) => {
	return (
		<div className="order-2 md:order-1">
			<div className="max-w-[550px] m-auto sticky top-8 space-y-4">
				{/* Preview Window */}
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					className="md:bg-gray-50 shadow-sm"
				>
					<div className="rounded-3xl max-h-[550px] overflow-hidden relative">
						<BlindRender
							renderFabric={renderFabric}
							renderHem={renderHem}
							renderValance={renderValance}
						/>
					</div>
				</motion.div>

				{/* Price Summary */}
				<div className="text-white md:text-gray-600 md:text-black md:bg-gray-50 rounded-3xl p-4 text-lg space-y-2">
					<div className="flex justify-between items-center">
						<span>Fabric Price</span>
						<span>{displayCountryPrice(originalPrices.fabricPrice)}</span>
					</div>

					{!isCellular && (
						<div className="flex justify-between items-center">
							<span>Bottom Bar Price</span>
							<span>Included</span>
						</div>
					)}

					{!isCellular && (
						<div className="flex justify-between items-center">
							<span>Control Price</span>
							<span>
								{displayCountryPrice(
									parseFloat(originalPrices.drivePrice) +
										parseFloat(originalPrices.remotePrice) +
										parseFloat(originalPrices.chargerPrice) +
										parseFloat(originalPrices.connectorPrice)
								)}
							</span>
						</div>
					)}

					{!isCellular && (
						<div className="flex justify-between items-center">
							<span>Valance Price</span>
							<span>
								{displayCountryPrice(
									parseFloat(originalPrices.valancePrice) +
										parseFloat(originalPrices.channelPrice)
								)}
							</span>
						</div>
					)}

					<div className="border-t border-gray-200" />

					<div className="flex justify-between items-center">
						<span>Total</span>
						<span className="line-through">
							{displayCountryPrice(totalPrice)}
						</span>
					</div>

					<div className="flex justify-between items-center text-primary">
						<span>
							You Save (
							{(((totalPrice - finalPrice) / totalPrice) * 100).toFixed(0)}%)
						</span>
						<span>- {displayCountryPrice(totalPrice - finalPrice)}</span>
					</div>

					<div className="border-t border-gray-200" />

					<div className="flex justify-between items-center">
						<span className="font-bold text-xl md:text-2xl">Your Price</span>
						<span className="text-xl md:text-2xl font-bold text-primary">
							{displayCountryPrice(finalPrice)}
						</span>
					</div>
				</div>
			</div>
		</div>
	);
};

const Customize_Reworked = ({ children }) => {
	/*=============================================================================*/
	/*===============================  VARIABLES  =================================*/
	/*=============================================================================*/
	const dispatch = useDispatch();
	const history = useNavigate();
	const { userInfo } = useSelector((state) => state.userLogin);
	const { productInfo, optionInfo } = useSelector(
		(state) => state.customize.customizeProduct
	);

	const country = useSelector((state) => state.country);
	const [totalPrice, setTotalPrice] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [renderFabric, setRenderFabric] = useState("");
	const [renderHem, setRenderHem] = useState("");
	const [renderValance, setRenderValance] = useState("");
	const [submitted, setSubmitted] = useState(false);
	const [allPrices, setAllPrices] = useState({});
	const location = useLocation();
	const isCellular = productInfo?.Type === "Simply Cellular";
	const params = new URLSearchParams(window.location.search);
	const defaultConfig = params.get("default");

	const steps = [
		{
			page: 1,
			name: "Details",
			url: "/details",
			info: "Select the width, length, and mount type of your shade.",
		},
		{
			page: 2,
			name: "Fabric",
			url: "/fabric",
			info: "Choose the fabric to be used for your shade.",
		},
		{
			page: 3,
			name: "Bottom Bar",
			url: "/bottombar",
			info: "Bottom Bars are used to weigh down the shade. Choose the bottom bar that best suits your needs.",
		},
		{
			page: 4,
			name: "Control",
			url: "/control",
			info: "Control is used to raise and lower the shade. Choose the control that best suits your needs.",
		},
		{
			page: 5,
			name: "Valance",
			url: "/valance",
			info: "Valance is used to cover the top of the shade. Choose the valance that best suits your needs.",
		},
		{
			page: 6,
			name: "Summary",
			url: "/summary",
			info: "Review your selections and add the shade to your cart.",
		},
	]
		.filter((step) => {
			if (isCellular) {
				return ![3, 4, 5].includes(step.page);
			} else {
				return step;
			}
		})
		.map((step, i) => {
			return {
				...step,
				page: i + 1,
			};
		});

	const isLastStep = currentPage === steps.length;

	/*=============================================================================*/
	/*===============================  USE EFFECT  ================================*/
	/*=============================================================================*/
	useEffect(() => {
		if (defaultConfig || (!productInfo && !optionInfo)) return;
		getPrice();
		!isCellular && hemRender();
		setRenderFabric(productInfo.Fabric + " " + productInfo.SG_Color);
		!isCellular &&
			valanceRender(productInfo.Valance, productInfo.Valance_Color);
	}, [productInfo, optionInfo, defaultConfig]);

	useEffect(() => {
		const page = steps.find(
			(step) => step.url === location.pathname.split("/customize")[1]
		)?.page;
		setCurrentPage(page);

		// Prevent access to 3,4,5 steps for cellular shades
		if (isCellular && !page) {
			history("/customize/details");
		}

		// Find widget-align-left class and hide it
		const widgetAlignLeft = document.querySelector(
			"#hubspot-messages-iframe-container"
		);
		if (widgetAlignLeft) {
			// Delete the element
			widgetAlignLeft.remove();
		}
	}, [location.pathname]);

	/*=============================================================================*/
	/*===============================  FUNCTIONS  =================================*/
	/*=============================================================================*/
	const hemRender = () => {
		let temp = "";
		if (
			productInfo.Hem_Wrap_Type == "Yes" &&
			productInfo.Bottom_Bar.includes("Accubar")
		)
			temp = "wab";
		else if (productInfo.Bottom_Bar.includes("Accubar")) temp = "ab";
		else if (productInfo.Bottom_Bar.includes("Plain Hem")) temp = "ph";
		else if (productInfo.Bottom_Bar.includes("Illusion")) temp = "ill";
		else if (productInfo.Bottom_Bar.includes("Interlude")) temp = "int";
		setRenderHem(
			temp +
				"/" +
				(productInfo.Bottom_Bar == "Plain Hem"
					? "standard"
					: productInfo.Bottom_Bar_Color)
		);
	};

	const valanceRender = (name, color) => {
		name = name?.toLowerCase().replace(" wrapped", "").replace("wrapped ", "");
		color = color?.toLowerCase();
		const nameMap = {
			"open roll": "or",
			'3" fascia': "3f",
			'4" fascia': "4f",
			"sb compact curved cassette": "ccc",
			"compact curved cassette": "ccc",
			"pvc valance": "pvc",
			'3" wrapped fascia': "3f",
			'4" wrapped fascia': "4f",
			"sb curved cassette": "ccc",
			"square cassette": "sc",
		};

		const shortenedName = nameMap[name] || "or";
		console.log(shortenedName);

		setRenderValance(`render/valance/${shortenedName}/${color}.png`);
	};

	function splitFullName(fullName) {
		if (typeof fullName !== "string") {
			return { fname: "Guest", lname: "" };
		}

		const nameRegex = /^(\S+)\s+(.+)$/;
		const match = fullName.trim().match(nameRegex);

		if (match) {
			return {
				fname: match[1],
				lname: match[2],
			};
		} else {
			return {
				fname: fullName,
				lname: "-",
			};
		}
	}

	const getPrice = async () => {
		let width = optionInfo.widthWholeFrac
			? parseFloat(optionInfo.widthWhole) +
			  parseFloat(optionInfo.widthWholeFrac)
			: optionInfo.widthWhole;
		let length = optionInfo.lengthWholeFrac
			? parseFloat(optionInfo.lengthWhole) +
			  parseFloat(optionInfo.lengthWholeFrac)
			: optionInfo.lengthWhole;

		if (length % 1 !== 0) {
			length = eval(length);
		}
		if (width % 1 !== 0) {
			width = eval(width);
		}

		let shade = productInfo.Type + " Shade";
		let priceGroup = productInfo.Fabric_Code ? productInfo.Price_Group : -1;
		let drive = productInfo.Operation;
		let valance = productInfo.Valance;
		let channel = productInfo.L_Channel;
		let quantity = optionInfo.quantity;
		let remote = productInfo.remote;
		let charger = productInfo.charger;
		let connector = productInfo.connector;

		try {
			let tp = await calculateTotalPrice(
				length,
				width,
				shade,
				priceGroup,
				drive,
				valance,
				channel,
				remote,
				charger,
				connector
			);

			console.log(tp, shade, productInfo);

			Object.keys(tp).forEach((key) => {
				tp[key] = Math.round(quantity * tp[key] * 1.5 * 100) / 100;
			});
			setAllPrices(tp);
			setTotalPrice(tp.totalPrice);
		} catch (error) {
			console.log(error);
			// TODO - Error handling
		}
	};

	const submitOrder = () => {
		if (!isLastStep) return;
		if (productInfo.error) {
			return alert(
				"Please re-check the measurement. The current size is not compatible."
			);
		}
		// Error handling
		if (
			!isCellular &&
			productInfo.Valance.includes("Open Roll") &&
			productInfo.Valance != "Open Roll"
		) {
			productInfo.Valance = "Open Roll";
			productInfo.Valance_Color = "-";
		}
		if (
			!isCellular &&
			productInfo.Valance != "Open Roll" &&
			(!productInfo.Valance_Color || productInfo.Valance_Color == "-")
		) {
			return alert("Please select a valance colour.");
		}
		if (
			!isCellular &&
			productInfo.Bottom_Bar_Color != "Plain Hem" &&
			(productInfo.Bottom_Bar_Color == "-" || !productInfo.Bottom_Bar_Color)
		) {
			return alert("Please select a bottom bar colour.");
		}
		if (userInfo?.email) {
			addEmailToCartMailChimp(
				userInfo?.email,
				"customizing shades",
				splitFullName(userInfo?.name)["fname"],
				splitFullName(userInfo?.name)["lname"]
			);
		}

		const itemId = optionInfo?.editId;
		if (itemId !== null) {
			dispatch(removeFromCart(itemId));
		}

		dispatch(
			addToCart({
				productInfo: {
					...productInfo,
					Program: "Customized " + productInfo.Type + " Shade",
					New_Fabric: productInfo.OCS_Name,
					specialInstruction: isCellular
						? "Cut Per Side: " +
						  getSimplyCutSize(
								parseFloat(optionInfo?.widthWhole),
								optionInfo.measure
						  )
						: "",
					New_Colour:
						productInfo?.OCS_Colour === ""
							? optionInfo?.selectedColor
							: productInfo?.OCS_Colour,
				},
				optionInfo: {
					...optionInfo,
					OUR_PRICE: OUR_PRICE,
					RETAIL_PRICE: totalPrice.toFixed(2),
				},
			})
		);

		setSubmitted(true);
		// Redirect after 1 second
		setTimeout(() => {
			window.location.href = "/cart";
		}, 1000);
	};

	const displayCountryPrice = (price) => {
		price = parseFloat(price) || 0;
		if (country == "USA") {
			return `$${(price * 0.75).toFixed(2)} USD`;
		} else {
			return `$${price.toFixed(2)} CAD`;
		}
	};

	const onStepClick = (step) => {
		console.log(step);
		// Get the params

		const params = new URLSearchParams(window.location.search);

		history("/customize" + step?.url + "?" + params);
	};

	/* Alex 6 Jan 2024 - Beat the inflation event */
	const calculatePrice = (product) => {
		// console.log(product)
		// First discount parses from database, every product has its own different discount
		const discount1 = parseFloat(product?.Discount) || 0;

		// Extra discount sets here, if you want to enable it, set extraDiscount > 0
		// For this event, we set a global 15% AFTER the first discount applies
		const discount2 = parseFloat(product?.Global_Discount) || 0; // Have to set to 0.15 for default, otherwise customized product (does not belong to any "program") will not have discount

		const savings1 = parseFloat((totalPrice * discount1).toFixed(2));
		const savings2 = parseFloat(
			((totalPrice - savings1) * discount2).toFixed(2)
		);
		const salePrice1 = parseFloat((totalPrice - savings1).toFixed(2));

		const finalPrice = parseFloat((salePrice1 - savings2).toFixed(2));
		const discountedAllPrices = {};
		if (allPrices) {
			Object.keys(allPrices).forEach((key) => {
				discountedAllPrices[key] = parseFloat(
					allPrices[key] * (1 - discount1) * (1 - discount2)
				).toFixed(2);
			});
		}
		return {
			discountedAllPrices,
			totalPrice,
			discount1,
			discount2,
			salePrice1,
			finalPrice,
			savings1,
			savings2,
		};
	};

	const {
		retailPrice: RETAIL_PRICE,
		discount1: DISCOUNT1,
		discount2: DISCOUNT2,
		salePrice1: SALE_PRICE1,
		finalPrice: OUR_PRICE,
		savings1: SAVINGS1,
		savings2: SAVINGS2,
		discountedAllPrices: ALL_PRICES,
	} = calculatePrice(productInfo);

	return (
		<div className="relative min-h-screen bg-white">
			<Helmet>
				<title>Design Your Perfect Window Shade | Off Cut Shades</title>
			</Helmet>

			{/* Steps Navigation Bar */}
			<div className="container mx-auto">
				<div className="py-3 md:px-4">
					<div className="flex items-center justify-between">
						<Stepper
							steps={steps}
							currentStep={currentPage}
							onStepClick={onStepClick}
						/>
					</div>
				</div>
			</div>

			{/* Main Content */}
			<div className="px-4 py-2 md:container mx-auto my-8">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
					{/* Left Side - Preview */}
					<div className="hidden md:block">
						<Breakdown
							renderFabric={renderFabric}
							renderHem={renderHem}
							renderValance={renderValance}
							originalPrices={allPrices} // Original component prices
							totalPrice={totalPrice} // Original total price
							finalPrice={OUR_PRICE} // Final discounted price
							displayCountryPrice={displayCountryPrice}
							isCellular={isCellular}
						/>
					</div>

					{/* Right Side - Form */}
					<div className="order-1 md:order-2">
						<AnimatePresence mode="wait">
							<motion.div
								key={currentPage}
								initial={{ opacity: 0, x: 20 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: -20 }}
								className="space-y-8"
							>
								{/* Content Area */}
								<div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
									<div className="mb-6">
										<h1 className="text-2xl font-bold mb-2">
											{steps[currentPage - 1]?.name}
										</h1>
										<p className="text-gray-600">
											{steps[currentPage - 1]?.info}
										</p>
									</div>

									<div className="space-y-6">{children}</div>
								</div>

								{/* Navigation */}
								<div className="hidden md:flex items-center justify-between gap-4">
									{currentPage > 1 && (
										<button
											onClick={() => onStepClick(steps[currentPage - 2])}
											className="flex items-center gap-2 px-8 py-3 border-2 border-gray-200 rounded-xl font-medium hover:border-primary/30 transition-colors group"
										>
											<ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
											Back
										</button>
									)}

									<button
										onClick={() =>
											isLastStep
												? submitOrder()
												: onStepClick(steps[currentPage])
										}
										className="flex items-center gap-2 px-8 py-3 bg-primary text-white rounded-xl font-medium shadow-lg ml-auto group"
									>
										{isLastStep ? "Add to Cart" : "Continue"}
										<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
									</button>
								</div>
							</motion.div>
						</AnimatePresence>
					</div>
				</div>
			</div>

			{/* Mobile Navigation */}
			<div className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 p-4 z-50">
				<div className="flex items-center justify-between">
					<Tooltip
						content={
							<Breakdown
								renderFabric={renderFabric}
								renderHem={renderHem}
								renderValance={renderValance}
								ALL_PRICES={ALL_PRICES}
								displayCountryPrice={displayCountryPrice}
								OUR_PRICE={OUR_PRICE}
								isCellular={isCellular}
							/>
						}
					>
						<button className="w-full flex items-start flex-col">
							<div className="text-sm text-gray-600">Total Price</div>
							<div className="text-xl font-bold text-primary">
								{displayCountryPrice(OUR_PRICE)}
							</div>
							<div className="text-xs text-gray-400">Touch to view more</div>
						</button>
					</Tooltip>

					<div className="flex gap-2">
						{currentPage > 1 && (
							<button
								onClick={() => onStepClick(steps[currentPage - 2])}
								className="px-6 py-2.5 border border-gray-200 rounded-xl text-gray-700"
							>
								Back
							</button>
						)}
						<button
							onClick={() =>
								isLastStep ? submitOrder() : onStepClick(steps[currentPage])
							}
							className="px-6 py-2.5 bg-primary text-white rounded-xl font-medium"
						>
							{isLastStep ? "Add to Cart" : "Continue"}
						</button>
					</div>
				</div>
			</div>

			{/* Success Toast */}
			<AnimatePresence>
				<Toast
					show={submitted}
					type="success"
					message="Your customized shade has been added to the cart!"
				/>
			</AnimatePresence>
		</div>
	);
};

export default Customize_Reworked;
